import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { CreditCard, Plus, Trash2, AlertTriangle, Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import './MembershipAdmin.css';

/**
 * MembershipSettingsCard — Club Settings → "Membership cards" (v35).
 *
 * The one place the feature is switched on: on/off, the season-end date,
 * and the gate ticket prices (name + £, add/remove rows, free allowed).
 * Until it's on, nothing membership-related exists anywhere else in the app.
 *
 * Season (Stuart, 17 Sept — year-agnostic): the admin picks a DAY and MONTH
 * once ("31 August"). The server stores the current season's date and rolls
 * it forward itself when it passes, clearing every club-member mark as it
 * goes; the admin then marks the new season's paid list. Nothing to remember
 * each year. The API still takes a full YYYY-MM-DD — this card sends the
 * next occurrence of the chosen day/month (by the server's London "today").
 */
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const daysIn = (month) => (month === 2 ? 28 : [4, 6, 9, 11].includes(month) ? 30 : 31);
// Next 'YYYY-MM-DD' for day/month on or after todayIso.
const nextOccurrence = (day, month, todayIso) => {
  if (!day || !month) return '';
  let y = Number(todayIso.slice(0, 4));
  const mk = (yy) => `${yy}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return mk(y) >= todayIso ? mk(y) : mk(y + 1);
};
const fmtLong = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
const poundsToPence = (v) => {
  const n = Number(String(v).replace(/[£,\s]/g, ''));
  return Number.isFinite(n) ? Math.max(0, Math.round(n * 100)) : NaN;
};
const penceToPounds = (p) => (Number(p || 0) / 100).toFixed(2).replace(/\.00$/, '');

export default function MembershipSettingsCard({ clubSlug }) {
  const qc = useQueryClient();
  const { refreshUser } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['membership-admin', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/membership/admin`).then(r => r.data),
  });

  const [enabled, setEnabled] = useState(false);
  const [seasonDay, setSeasonDay] = useState('');
  const [seasonMonth, setSeasonMonth] = useState('');
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null); // { cleared }
  const [error, setError] = useState('');

  // Hydrate from the server on first data and after that ONLY while the form
  // is clean — a background refetch must not wipe unsaved edits (QA #4).
  // 17 Sept bug: "clean" was judged BEFORE the first hydration, so a club with
  // cards ON never hydrated on a remount (form said off/blank while the server
  // said on — and a Save from that state would have switched cards off).
  // `hydratedRef` makes the first fill unconditional.
  const hydratedRef = useRef(false);
  const dirtyRef = useRef(false);
  const serverSig = data ? JSON.stringify([!!data.enabled, data.season_ends || '', data.ticket_prices || []]) : '';
  useEffect(() => {
    if (!data) return;
    if (hydratedRef.current && dirtyRef.current) return;
    setEnabled(!!data.enabled);
    setSeasonDay(data.season_ends ? String(Number(data.season_ends.slice(8, 10))) : '');
    setSeasonMonth(data.season_ends ? String(Number(data.season_ends.slice(5, 7))) : '');
    setRows((data.ticket_prices || []).map(p => ({ label: p.label, pounds: penceToPounds(p.pence) })));
    hydratedRef.current = true;
    // eslint-disable-next-line
  }, [serverSig]);

  const today = data?.today || new Date().toISOString().slice(0, 10);
  const seasonEnds = nextOccurrence(Number(seasonDay), Number(seasonMonth), today);

  const dirty = hydratedRef.current && data && (
    enabled !== !!data.enabled
    || (seasonEnds || '') !== (data.season_ends || '')
    || JSON.stringify(rows.map(r => ({ label: r.label.trim(), pence: poundsToPence(r.pounds || '0') })))
       !== JSON.stringify((data.ticket_prices || []).map(p => ({ label: p.label, pence: p.pence })))
  );
  dirtyRef.current = !!dirty;

  const save = async () => {
    setError(''); setSaved(null);
    if (enabled && !seasonEnds) { setError('Set the date the season ends — every card is valid until then.'); return; }
    const ticketPrices = [];
    for (const r of rows) {
      const label = r.label.trim();
      const pence = poundsToPence(r.pounds === '' ? '0' : r.pounds);
      if (!label) { setError('Every ticket type needs a name (e.g. Adult, Concession, U16).'); return; }
      if (Number.isNaN(pence)) { setError(`"${label}" needs a price — put 0 for free.`); return; }
      ticketPrices.push({ label, pence });
    }
    if (!enabled && data.enabled && (data.members || 0) > 0
      && !window.confirm(`Switching membership cards off hides the card for ${data.members} club member${data.members === 1 ? '' : 's'} and the Scan page for your club staff. Their marks are kept until the season ends. Continue?`)) return;
    setSaving(true);
    try {
      const res = await axios.patch(`/clubs/${clubSlug}/membership/settings`, {
        enabled,
        season_ends: seasonEnds || null,
        ticket_prices: ticketPrices,
      });
      qc.setQueryData(['membership-admin', clubSlug], res.data);
      qc.invalidateQueries({ queryKey: ['club', clubSlug] });
      qc.invalidateQueries({ queryKey: ['members', clubSlug] });
      // The Wallet's Scan cards button and the Wallet card key off /auth/me.
      if (refreshUser) refreshUser().catch(() => {});
      setSaved({ cleared: res.data.cleared || 0 });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save membership settings.');
    } finally { setSaving(false); }
  };

  return (
    <div className="card settings-card" style={{ marginTop: 16 }} data-tour="settings-membership">
      <h3 style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}><CreditCard size={18} /> Membership cards</h3>
      <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>
        Everyone in the app is a <strong>community member</strong> — comms, fixtures and sponsor offers.{' '}
        <strong>Club members</strong> are the people who've paid their membership: they also get a live
        club card in their Wallet for free entry to home games and member treatment at the bar.{' '}
        <strong>Club staff</strong> — whoever works the gate or the bar — scan it from their Wallet (staff can be community
        and club members too); you see attendance and gate money per match under Reports. You still
        take the money and keep your register as you do now — Marakin just reflects who's paid.
      </p>

      {isLoading && <div className="page-loading" style={{ minHeight: 80 }}><div className="spinner" /></div>}
      {data && (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 15, fontWeight: 600 }}>
            <input type="checkbox" checked={enabled} onChange={e => { setEnabled(e.target.checked); setSaved(null); }} style={{ width: 18, height: 18, cursor: 'pointer' }} />
            Membership cards are {enabled ? 'on' : 'off'} for this club
          </label>
          {data.enabled && (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '6px 0 0 28px' }}>
              {data.members} club member{data.members === 1 ? '' : 's'} marked · {data.scanners} club staff can scan
              — both set in <strong>Members</strong> (the Club member and Club staff pills).
            </p>
          )}

          <div className="form-group" style={{ marginTop: 18 }}>
            <label className="label">Season ends every year on</label>
            <div style={{ display: 'flex', gap: 8, maxWidth: 360 }}>
              <select className="input" value={seasonDay} aria-label="Day" style={{ flex: '0 0 92px' }}
                onChange={e => { setSeasonDay(e.target.value); setSaved(null); }}>
                <option value="">Day</option>
                {Array.from({ length: daysIn(Number(seasonMonth) || 1) }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <select className="input" value={seasonMonth} aria-label="Month" style={{ flex: 1, minWidth: 0 }}
                onChange={e => { const m = Number(e.target.value); setSeasonMonth(e.target.value); if (Number(seasonDay) > daysIn(m || 1)) setSeasonDay(String(daysIn(m || 1))); setSaved(null); }}>
                <option value="">Month</option>
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
              Every card runs to this date each year, then lapses. When it passes, the club-member marks clear
              and you mark each membership status again for the new season — nothing to remember.
              {seasonEnds && <> This season ends <strong>{fmtLong(seasonEnds)}</strong>.</>}
            </p>
            {data.season_ends && seasonEnds && seasonEnds < data.season_ends && (
              <div className="madm-note is-warn">
                <AlertTriangle size={15} /> Ending the season earlier than {fmtLong(data.season_ends)}: cards lapse on {fmtLong(seasonEnds)} and the marks clear then.
              </div>
            )}
          </div>

          <div style={{ marginTop: 18 }}>
            <label className="label">Gate ticket prices <span className="optional">(non-members paying on the day — a tally, not a till)</span></label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
              {rows.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input className="input" placeholder="e.g. Adult" value={r.label} style={{ flex: 2, minWidth: 0 }}
                    onChange={e => { const v = e.target.value; setRows(rs => rs.map((x, j) => (j === i ? { ...x, label: v } : x))); setSaved(null); }} />
                  <div style={{ position: 'relative', flex: 1, minWidth: 90 }}>
                    <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}>£</span>
                    <input className="input" inputMode="decimal" placeholder="0" value={r.pounds} style={{ paddingLeft: 26, width: '100%' }}
                      onChange={e => { const v = e.target.value; setRows(rs => rs.map((x, j) => (j === i ? { ...x, pounds: v } : x))); setSaved(null); }} />
                  </div>
                  <button type="button" className="btn btn-ghost btn-sm btn-icon btn-danger-text" title="Remove"
                    onClick={() => { setRows(rs => rs.filter((_, j) => j !== i)); setSaved(null); }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {rows.length < 8 && (
                <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
                  onClick={() => { setRows(rs => [...rs, { label: '', pounds: '' }]); setSaved(null); }}>
                  <Plus size={14} /> Add ticket type
                </button>
              )}
              {rows.length === 0 && <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>No prices yet — e.g. Adult £5 · Concession £3 · U16 free (£0).</p>}
            </div>
          </div>

          {error && <div className="madm-note is-error">{error}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
            <button className="btn btn-primary" onClick={save} disabled={saving || !dirty}>
              {saving ? 'Saving…' : 'Save membership settings'}
            </button>
            {saved && !dirty && (
              <span style={{ fontSize: 13, color: 'var(--success)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Check size={14} /> Saved{saved.cleared ? ` — ${saved.cleared} member mark${saved.cleared === 1 ? '' : 's'} cleared for the new season` : ''}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
